import type { Ref } from 'react';
import { useState, ReactNode } from 'react';
import { TableColumn } from '../../../types/reports';
import TableHeader from './TableHeader';
import TableRow from './TableRow';
import TableFooter from './TableFooter';
import ShowMoreRow from './ShowMoreRow';
import { generateReportsTableItemKey } from '../utils/reportItemKeys';
import s from './ReportsTable.scss';

interface ReportsTableProps<T> {
  tableKey?: string;
  tableRef?: Ref<HTMLTableElement>;
  columns: TableColumn[];
  data: T[];
  renderRow: (item: T, index?: number) => ReactNode;
  renderFooter?: (data: T) => ReactNode;
  initialVisibleCount?: number;
}

const ReportsTable = <T,>({
  tableKey,
  tableRef,
  columns,
  data,
  renderRow,
  renderFooter,
  initialVisibleCount,
}: ReportsTableProps<T>) => {
  const [showAll, setShowAll] = useState<boolean>(initialVisibleCount === undefined);

  const handleShowMore = () => {
    setShowAll(true);
  };

  const totalRow = (data as any[]).find(
    row =>
      row?.productType === 'Total' || row?.productName === 'Total' || row?.exclusiveType === 'total' // •`_´•
  );
  const actualRows = data.filter(row => row !== totalRow);
  const displayedData =
    showAll || initialVisibleCount === undefined
      ? actualRows
      : actualRows.slice(0, initialVisibleCount);
  const showMore =
    !showAll && initialVisibleCount !== undefined && data.length > initialVisibleCount;

  return (
    <table key={tableKey} ref={tableRef} className={s['root']}>
      <TableHeader columns={columns} />
      <tbody>
        {displayedData.map((item, index) => (
          <TableRow
            key={generateReportsTableItemKey(item, index)}
            index={index}
            rowData={item}
            renderRow={renderRow}
          />
        ))}
        {showMore && <ShowMoreRow className={s['show-more']} onClick={handleShowMore} />}
      </tbody>
      {!showMore && renderFooter && (
        <TableFooter footerData={totalRow} renderFooter={renderFooter} />
      )}
    </table>
  );
};

export default ReportsTable;
